import React from 'react'
import { useTranslation } from 'react-i18next'
import KibDivider from '@chewy/kib-divider-react'
import { KibContainer } from '@chewy/kib-layout-react'
import { NumberUtils } from '@pbt/pbt-ui-components'

import { PaymentMethod } from '~/constants/addPaymentDialogV2Constants'

import { usePaymentDialog } from '../context/PaymentDialogProvider'
import { usePaymentMethodTranslations } from '../utils/paymentMethodTranslations'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-layout-styles/dist/styles/kib-container.css'
import styles from './PaymentSuccessScreen.module.scss'

export const PaymentSuccessScreen = () => {
  const { t } = useTranslation(['Invoices', 'Dialogs', 'Common'])
  const { dialogState } = usePaymentDialog()
  const methodTranslations = usePaymentMethodTranslations()
  const { paymentData } = dialogState

  const getActualAmountCharged = (): number => {
    const parsed = parseFloat(paymentData.amountToCharge)
    const amountFromPayment = Number.isNaN(parsed) ? 0 : parsed
    const accountCreditAmount = paymentData.accountCreditTotal || 0
    const totalPaid = amountFromPayment + accountCreditAmount

    return totalPaid
  }

  const getRegularPaymentMethodDisplay = (baseMethod: string): string => {
    if (
      baseMethod === PaymentMethod.CREDIT_DEBIT &&
      paymentData.creditCardType &&
      paymentData.creditCardLastFour
    ) {
      return t('Dialogs:ADD_PAYMENT_DIALOG_V2.CREDIT_DEBIT_SUCCESS', {
        method: t('Dialogs:ADD_PAYMENT_DIALOG_V2.CREDIT_CARD'),
        creditCardType: paymentData.creditCardType,
        creditLastFour: paymentData.creditCardLastFour,
      })
    }

    if (baseMethod === PaymentMethod.CARECREDIT && paymentData.authNumber) {
      return t('Dialogs:ADD_PAYMENT_DIALOG_V2.CARECREDIT_SUCCESS', {
        authNumber: paymentData.authNumber,
      })
    }
    if (baseMethod === PaymentMethod.CHECK && paymentData.checkNumber) {
      return t('Dialogs:ADD_PAYMENT_DIALOG_V2.CHECK_SUCCESS', {
        checkNumber: paymentData.checkNumber,
      })
    }
    if (baseMethod === PaymentMethod.CASH) {
      return t('Common:PAYMENTS.CASH')
    }
    if (baseMethod === PaymentMethod.CREDIT_DEBIT) {
      return t('Dialogs:ADD_PAYMENT_DIALOG_V2.CREDIT_CARD')
    }
    return methodTranslations[baseMethod as PaymentMethod] || baseMethod
  }

  const getPaymentMethodDisplay = (): React.ReactNode => {
    const accountCreditAmount = paymentData.accountCreditTotal || 0
    const parsed = parseFloat(paymentData.amountToCharge)
    const paymentMethodAmount = Number.isNaN(parsed) ? 0 : parsed
    const baseMethod = paymentData.paymentMethod

    const accountCreditLine = (
      <div>
        {t(
          'Dialogs:ADD_PAYMENT_DIALOG_V2.CREDIT_APPLIED_TOOLTIP_TITLE_PAYMENT',
        )}
        : {NumberUtils.formatMoney(accountCreditAmount)}
      </div>
    )

    const paymentMethodLine = (
      <div>
        {getRegularPaymentMethodDisplay(baseMethod)}:{' '}
        {NumberUtils.formatMoney(paymentMethodAmount)}
      </div>
    )

    if (accountCreditAmount > 0 && paymentMethodAmount > 0) {
      return (
        <div>
          {accountCreditLine}
          {paymentMethodLine}
        </div>
      )
    }

    if (accountCreditAmount > 0 && paymentMethodAmount === 0) {
      return accountCreditLine
    }

    return getRegularPaymentMethodDisplay(baseMethod)
  }

  return (
    <KibContainer className={styles.paymentSuccessContainer}>
      <div className={styles.paymentSuccessTitle}>
        {t('Invoices:PAYMENTS.PAYMENT_DETAILS.PAYMENT_DETAILS')}
      </div>
      <div className={styles.paymentSuccessInfoRow}>
        <div className={styles.paymentSuccessInfoRowLabel}>
          {t('Dialogs:ADD_PAYMENT_DIALOG_V2.AMOUNT_CHARGED')}
        </div>
        <div className={styles.paymentSuccessInfoRowValue}>
          {NumberUtils.formatMoney(getActualAmountCharged())}
        </div>
      </div>
      <KibDivider className={styles.paymentSuccessDivider} color="alternate" />
      <div className={styles.paymentSuccessInfoRow}>
        <div className={styles.paymentSuccessInfoRowLabel}>
          {t('Common:PAYMENTS.PAYMENT_METHOD')}
        </div>
        <div className={styles.paymentSuccessInfoRowValue}>
          {getPaymentMethodDisplay()}
        </div>
      </div>
      <KibDivider className={styles.paymentSuccessDivider} color="alternate" />
      {paymentData.paymentMethod === PaymentMethod.CASH ? (
        <>
          <div className={styles.paymentSuccessInfoRow}>
            <div className={styles.paymentSuccessInfoRowLabel}>
              {t('Dialogs:ADD_PAYMENT_DIALOG_V2.AMOUNT_RECEIVED')}
            </div>
            <div className={styles.paymentSuccessInfoRowValue}>
              {NumberUtils.formatMoney(
                parseFloat(paymentData.amountReceived || '0'),
              )}
            </div>
          </div>
          <KibDivider
            className={styles.paymentSuccessDivider}
            color="alternate"
          />
          <div className={styles.paymentSuccessInfoRow}>
            <div className={styles.paymentSuccessInfoRowLabel}>
              {t('Common:CHANGE_DUE')}
            </div>
            <div className={styles.paymentSuccessInfoRowValue}>
              {NumberUtils.formatMoney(
                parseFloat(paymentData.changeDue || '0'),
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={styles.paymentSuccessInfoRow}>
            <div className={styles.paymentSuccessInfoRowLabel}>
              {t('Dialogs:ADD_PAYMENT_DIALOG_V2.REMAINING_INVOICE_BALANCE')}
            </div>
            <div className={styles.paymentSuccessInfoRowValue}>
              {NumberUtils.formatMoney(0)}
            </div>
          </div>
        </>
      )}
      <KibDivider className={styles.paymentSuccessDivider} color="alternate" />
    </KibContainer>
  )
}
